// Business Logic Layer - pure functions, no React. Mirrors
// _features/company/modals/CompanyFormModal/utils/companyFormRules.js's
// shape: schema post-processing + success-message formatting, kept out of
// the UI/state layers.

// A market can genuinely have more than one country (see
// marketRowMapping.js's mapMarketRowForEdit, which always shapes `country`
// as an array) - forced to "multiSelect" here rather than trusting
// whatever type the org's object-customization record for this field
// happens to have, since renderInput.jsx's singleSelect/listManager cases
// each read stored values through a different, incompatible convention
// (getValueWithDefault vs. getArrayValueWithDefault, valueSelector
// defaulting to "value" vs. "id") and only the multiSelect case's
// convention actually matches the `[{ value }]` shape produced above.
// isObject/valueSelector are pinned for the same reason - not left to
// possibly-unset per-field customization data.
function forceCountryMultiSelect(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      field.name === "country"
        ? { ...field, type: "multiSelect", isObject: true, valueSelector: "value" }
        : field
    ),
  }));
}

// Display-only relabel - the field's `name` (and whatever it's stored as)
// stays "description", only the label shown to the user changes. Matches
// the Liquid Glass design's own naming for this same free-text field
// (companies-workspace.dc.html's market "New market" quick-add spec calls
// it "Notes", not "Description"). Also pinned to a single row here (see
// TextArea's own `rows` default of 4, threaded through by FormField.jsx's
// `rows={field.rows}`) - the design's own Notes box is the same height as
// every other field's single-line input, not a multi-line paragraph box.
function relabelDescriptionToNotes(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      field.name === "description" ? { ...field, label: "Notes", rows: 1 } : field
    ),
  }));
}

// Design-example placeholders (companies-workspace.dc.html ~line 2489's
// `fld("Market name", "Canada")` / `fld("Notes", "Launch target Q1")`) -
// neither field had one at all before. Status also separately fixed here:
// its org-customization record carries an explicit `placeholder: ""`,
// which - unlike a missing/undefined placeholder - overrides Select's own
// "Select" default (see FormField.jsx's `commonProps.placeholder`), so it
// rendered as a blank trigger while Country/Currency (no placeholder set)
// correctly fell back to "Select".
const FIELD_PLACEHOLDERS = {
  marketName: "Canada",
  description: "Launch target Q1",
  status: "Select",
};
function applyFieldPlaceholders(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      FIELD_PLACEHOLDERS[field.name] !== undefined
        ? { ...field, placeholder: FIELD_PLACEHOLDERS[field.name] }
        : field
    ),
  }));
}

// The design's own "New market" quick-add panel (companies-workspace.dc.html
// ~line 2489) lays fields into a 2-column grid with only the free-text
// field spanning both columns, pinned last so every other field - including
// country, which reads fine as a couple of chips at half width - packs
// tightly two-per-row first (`fld(name),sel(currency),sel(tax),sel(status),
// fld(notes,"span 2")`). Mirrored here by a stable sort, not just a style
// change. See DynamicForm/index.jsx's FULL_WIDTH_FIELD_TYPES (the same
// "only textArea" rule) and MarketFormModal/index.jsx's `market-form-modal`
// grid override for the two other halves this pairs with.
function packFieldsForTwoColumnGrid(sections) {
  const isFullWidth = (field) => field.type === "textArea";
  return sections.map((section) => {
    const fields = section.fields || [];
    return {
      ...section,
      fields: [...fields.filter((f) => !isFullWidth(f)), ...fields.filter((f) => isFullWidth(f))],
    };
  });
}

// The design's Add-drawer has no "Market details"-style heading at all -
// just the field grid straight under the modal's own title/blurb (see
// MarketFormModal/index.jsx's `title`/`description`). Blanking the group
// name here (rather than deleting the section) keeps every field's
// grouping/order intact - DynamicForm/index.jsx already skips rendering the
// header block entirely when `section.section` is falsy, so this removes
// the heading's own vertical space too, not just its text.
function stripSectionHeading(sections) {
  return sections.map((section) => ({ ...section, section: "" }));
}

/**
 * Applies every MarketFormModal-specific schema post-processing rule to
 * the raw org-customization schema. No more mode-dependent referenceType
 * swap here - the "marketCountries" referenceType (useResolveFormSchema.js)
 * now always returns every country, annotated per-item with whether
 * another market already has it, so one reference source correctly serves
 * both create and edit mode.
 */
export function processMarketFormSchema(schema) {
  return stripSectionHeading(
    packFieldsForTwoColumnGrid(
      applyFieldPlaceholders(relabelDescriptionToNotes(forceCountryMultiSelect(schema)))
    )
  );
}

/**
 * Derives the create/update success-toast message from a mutation
 * response - same "{marketName} - Market successfully {created/updated}"
 * rule the original inline handlers duplicated once for create and once
 * for edit.
 */
export function formatMarketSuccessMessage(response, mode) {
  return `${response?.marketName} - Market successfully ${mode === "create" ? "created" : "updated"}`;
}
